import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import { Save, Coffee, Plus, Trash2, Monitor, AlertTriangle, ArrowRight, Lightbulb } from "lucide-react";
import { toast } from "sonner";

// Owner-only Pauze-instellingen — cleaner sectioned layout with live preview.
export default function PauseSettings() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const isOwner = user?.role === "owner";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company/settings");
        setSettings(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, []);

  if (!settings) return <div className="skeleton h-96 rounded-3xl m-6" />;

  const upd = (k) => (v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        auto_pause_enabled: !!settings.auto_pause_enabled,
        auto_pause_mode: settings.auto_pause_mode || "flat",
        auto_pause_threshold_hours: settings.auto_pause_threshold_hours ?? 5,
        auto_pause_flat_minutes: settings.auto_pause_flat_minutes ?? 30,
        auto_pause_tiers: settings.auto_pause_tiers || [],
        pause_warning_ratio_percent: settings.pause_warning_ratio_percent ?? 5,
      };
      const { data } = await api.patch("/company/settings", payload);
      setSettings(data);
      toast.success(t("settings_saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 pb-10 max-w-5xl" data-testid="page-pause-settings">
      <PageHeader
        eyebrow={t("admin_label")}
        title={t("pause_settings")}
        subtitle={t("pause_settings_desc")}
        actions={
          isOwner ? (
            <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="set-pause-save">
              <Save size={14} /> {busy ? t("saving") : t("save")}
            </button>
          ) : null
        }
      />

      {/* Hero summary — laat direct de kern-instelling zien */}
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="card p-5 mb-6 flex items-center gap-4"
        style={{ background: "linear-gradient(135deg, hsl(38 92% 96%) 0%, hsl(var(--surface)) 60%)" }}
        data-testid="pause-hero"
      >
        <div
          className="w-12 h-12 rounded-2xl grid place-items-center shrink-0 text-white"
          style={{
            background: settings.auto_pause_enabled
              ? "linear-gradient(135deg, hsl(28 92% 55%) 0%, hsl(38 92% 45%) 100%)"
              : "linear-gradient(135deg, hsl(220 8% 55%) 0%, hsl(220 8% 40%) 100%)",
            boxShadow: settings.auto_pause_enabled
              ? "0 12px 28px -10px hsl(28 92% 45% / 0.5)"
              : "0 8px 20px -10px hsl(220 8% 40% / 0.4)",
          }}
        >
          <Coffee size={20} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))] leading-tight">
            {settings.auto_pause_enabled
              ? (settings.auto_pause_mode === "tiered" ? t("pause_mode_tiered") : t("pause_mode_flat"))
              : t("disabled_label")}
          </div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">
            {settings.auto_pause_enabled
              ? settings.auto_pause_mode === "flat"
                  ? `${settings.auto_pause_flat_minutes ?? 30} min pauze bij ≥ ${settings.auto_pause_threshold_hours ?? 5}u`
                  : `${(settings.auto_pause_tiers || []).length} ${t("pause_tiers_count") || "regels"}`
              : t("pause_disabled_note") || "Diensten krijgen geen automatische pauze"}
          </div>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[11px] font-bold ${
            settings.auto_pause_enabled ? "bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${settings.auto_pause_enabled ? "bg-[hsl(var(--success))] animate-pulse" : "bg-[hsl(var(--text-muted))]"}`} />
          {settings.auto_pause_enabled ? (t("active") || "Actief") : (t("inactive") || "Inactief")}
        </div>
      </motion.div>

      <div className="mt-2 mb-6 inline-flex items-center gap-1.5 text-[11px] text-[hsl(var(--text-muted))] font-medium">
        <Monitor size={12} strokeWidth={2} /> {t("pause_desktop_only_hint")}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          {/* Section 1 — enable/disable */}
          <Section testid="pause-section-enable">
            <SectionHeader
              step={1}
              title={t("auto_pause_enabled")}
              hint={t("auto_pause_enabled_hint")}
            />
            <Toggle
              disabled={!isOwner}
              testid="tog-auto-pause"
              value={!!settings.auto_pause_enabled}
              onChange={upd("auto_pause_enabled")}
              onLabel={t("enabled_label")}
              offLabel={t("disabled_label")}
            />
          </Section>

          {settings.auto_pause_enabled && (
            <>
              {/* Section 2 — calculation mode */}
              <Section testid="pause-section-mode">
                <SectionHeader
                  step={2}
                  title={t("pause_mode_title")}
                  hint={t("pause_mode_hint")}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ModeCard
                    active={settings.auto_pause_mode === "flat"}
                    disabled={!isOwner}
                    onClick={() => upd("auto_pause_mode")("flat")}
                    testid="pause-mode-flat"
                    title={t("pause_mode_flat")}
                    hint={t("pause_mode_flat_hint")}
                  />
                  <ModeCard
                    active={settings.auto_pause_mode === "tiered"}
                    disabled={!isOwner}
                    onClick={() => upd("auto_pause_mode")("tiered")}
                    testid="pause-mode-tiered"
                    title={t("pause_mode_tiered")}
                    hint={t("pause_mode_tiered_hint")}
                  />
                </div>
              </Section>

              {/* Section 3 — configuration */}
              <Section testid="pause-section-config">
                <SectionHeader
                  step={3}
                  title={t("pause_config_title")}
                  hint={settings.auto_pause_mode === "tiered" ? t("pause_config_hint_tiered") : t("pause_config_hint_flat")}
                />
                {settings.auto_pause_mode !== "tiered" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={t("pause_threshold_hours")} suffix={t("hours_short")}>
                      <input
                        type="number" min={0.5} max={24} step={0.5}
                        disabled={!isOwner}
                        data-testid="pause-threshold-hours"
                        value={settings.auto_pause_threshold_hours ?? 5}
                        onChange={(e) => upd("auto_pause_threshold_hours")(parseFloat(e.target.value) || 0)}
                        className="input-field tabular-nums"
                      />
                    </Field>
                    <Field label={t("pause_flat_minutes")} suffix={t("minutes_short")}>
                      <input
                        type="number" min={0} max={240} step={5}
                        disabled={!isOwner}
                        data-testid="pause-flat-minutes"
                        value={settings.auto_pause_flat_minutes ?? 30}
                        onChange={(e) => upd("auto_pause_flat_minutes")(parseInt(e.target.value, 10) || 0)}
                        className="input-field tabular-nums"
                      />
                    </Field>
                  </div>
                ) : (
                  <PauseTiersEditor
                    disabled={!isOwner}
                    tiers={settings.auto_pause_tiers || []}
                    onChange={(next) => upd("auto_pause_tiers")(next)}
                    t={t}
                  />
                )}
              </Section>

              {/* Section 4 — warning threshold */}
              <Section testid="pause-section-warning">
                <SectionHeader
                  step={4}
                  title={t("pause_warning_title_v2") || "Waarschuwing bij te weinig pauze"}
                  hint={t("pause_warning_hint_v2") || "Als iemand een pauze invoert die veel lager is dan je bedrijfsregel, laten we je een popup zien voordat je de dienst opslaat — zo voorkom je typefouten of overtredingen."}
                />
                <PauseWarningEditor
                  disabled={!isOwner}
                  value={settings.pause_warning_ratio_percent ?? 5}
                  onChange={upd("pause_warning_ratio_percent")}
                  t={t}
                />
              </Section>
            </>
          )}
        </div>

        {/* Live preview — right column on desktop */}
        <PausePreview settings={settings} t={t} />
      </div>
    </div>
  );
}

// ---------- Layout helpers ----------
function Section({ children, testid }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
      className="card p-5 md:p-6" data-testid={testid}
    >
      {children}
    </motion.section>
  );
}

function SectionHeader({ step, title, hint }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <span className="w-7 h-7 rounded-full bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center text-[12px] font-bold tabular-nums shrink-0">
        {step}
      </span>
      <div className="min-w-0">
        <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))] leading-tight">{title}</div>
        {hint && <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 leading-snug">{hint}</div>}
      </div>
    </div>
  );
}

function Field({ label, suffix, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--text-muted))]">{label}</span>
      <div className="relative">
        {children}
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-medium text-[hsl(var(--text-muted))] pointer-events-none tabular-nums">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

function ModeCard({ active, disabled, onClick, testid, title, hint }) {
  return (
    <button
      type="button" disabled={disabled} onClick={onClick} data-testid={testid}
      className={`text-left p-4 rounded-2xl border transition-all
        ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] shadow-[var(--shadow-xs)]" : "hairline bg-white hover:bg-[hsl(var(--muted))]"}
        ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]" : "border-[hsl(var(--border))]"}`}>
          {active && <span className="block w-1.5 h-1.5 rounded-full bg-white m-auto mt-[3px]" />}
        </span>
        <span className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{title}</span>
      </div>
      <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1.5 leading-snug">{hint}</div>
    </button>
  );
}

function Toggle({ value, onChange, disabled, testid, onLabel, offLabel }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!value)}
      data-testid={testid}
      disabled={disabled}
      className={`w-full flex items-center gap-3 rounded-2xl border hairline p-4 text-left transition-all
        ${value ? "bg-[hsl(var(--primary-soft))] border-[hsl(var(--primary))]" : "bg-white hover:bg-[hsl(var(--muted))]"}
        ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <span className={`inline-flex w-11 h-6 rounded-full transition items-center shrink-0
        ${value ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]"}`}>
        <span className={`w-5 h-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.15)] transition-transform mx-0.5
          ${value ? "translate-x-5" : "translate-x-0"}`} />
      </span>
      <span className={`text-[13px] font-semibold ${value ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--text-muted))]"}`}>
        {value ? onLabel : offLabel}
      </span>
    </button>
  );
}

// ---------- Tiers editor ----------
function PauseTiersEditor({ tiers, onChange, disabled, t }) {
  const setTier = (idx, key, value) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const addTier = () => {
    const last = tiers[tiers.length - 1];
    const hours = last ? Math.min(24, (last.hours || 0) + 2) : 5;
    onChange([...tiers, { hours, minutes: 15 }]);
  };
  const removeTier = (idx) => onChange(tiers.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2" data-testid="pause-tiers-editor">
      {tiers.map((tier, idx) => (
        <div
          key={idx}
          className="flex items-center gap-3 p-3 rounded-2xl border hairline bg-white hover:border-[hsl(var(--primary))]/40 transition"
          data-testid={`pause-tier-${idx}`}
        >
          <span className="w-7 h-7 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] grid place-items-center text-[11px] font-bold tabular-nums shrink-0">
            {idx + 1}
          </span>
          <span className="text-[12px] font-medium text-[hsl(var(--text-muted))] shrink-0">{t("pause_tier_from")}</span>
          <div className="relative">
            <input
              type="number" min={0} max={24} step={0.5}
              disabled={disabled}
              value={tier.hours}
              onChange={(e) => setTier(idx, "hours", parseFloat(e.target.value) || 0)}
              className="input-field !w-24 tabular-nums pr-8"
              data-testid={`pause-tier-hours-${idx}`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[hsl(var(--text-muted))] pointer-events-none">
              {t("hours_short")}
            </span>
          </div>
          <ArrowRight size={14} className="text-[hsl(var(--text-muted))] shrink-0" />
          <div className="relative">
            <input
              type="number" min={0} max={240} step={5}
              disabled={disabled}
              value={tier.minutes}
              onChange={(e) => setTier(idx, "minutes", parseInt(e.target.value, 10) || 0)}
              className="input-field !w-24 tabular-nums pr-8"
              data-testid={`pause-tier-minutes-${idx}`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[hsl(var(--text-muted))] pointer-events-none">
              min
            </span>
          </div>
          <button
            type="button" disabled={disabled} onClick={() => removeTier(idx)}
            className="ml-auto w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--danger-soft))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))] transition-colors disabled:opacity-40"
            data-testid={`pause-tier-remove-${idx}`}
            aria-label={t("delete")}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {tiers.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 text-center text-[12px] text-[hsl(var(--text-muted))]">
          {t("pause_tiers_empty")}
        </div>
      )}
      <button
        type="button" disabled={disabled} onClick={addTier}
        className="w-full h-11 flex items-center justify-center gap-1.5 rounded-2xl border border-dashed border-[hsl(var(--border))] text-[13px] font-semibold text-[hsl(var(--text-muted))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-soft))] transition-all"
        data-testid="pause-tier-add"
      >
        <Plus size={13} /> {t("pause_tier_add")}
      </button>
    </div>
  );
}

// ---------- Warning threshold editor (step 4) ----------
// Slider + concrete "at 8h shift, warn below X min" example — replaces the
// bare percentage input that everyone confused with an actual pause length.
function PauseWarningEditor({ value, onChange, disabled, t }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const off = pct === 0;
  // Use an 8-hour reference shift for the human-readable example.
  const refShiftHours = 8;
  const minBelow = Math.round((pct / 100) * refShiftHours * 60);

  return (
    <div className="space-y-4" data-testid="pause-warning-editor">
      {/* Off / on switch */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button" disabled={disabled}
          onClick={() => onChange(off ? 5 : 0)}
          data-testid="pause-warning-onoff"
          className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12.5px] font-bold transition
            ${off
              ? "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"
              : "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"}
          `}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${off ? "bg-[hsl(var(--text-muted))]" : "bg-[hsl(var(--primary))] animate-pulse"}`} />
          {off ? (t("pause_warning_off_label") || "Waarschuwing uit") : (t("pause_warning_on_label") || "Waarschuwing aan")}
        </button>
        {off && (
          <span className="text-[11.5px] text-[hsl(var(--text-muted))]">
            {t("pause_warning_off_hint") || "Je krijgt geen bevestigingspopup meer, ook niet bij 0 min pauze."}
          </span>
        )}
      </div>

      {!off && (
        <>
          {/* Slider row */}
          <div className="rounded-2xl border hairline bg-[hsl(var(--bg))] p-4">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">
                {t("pause_warning_slider_label") || "Waarschuw als pauze lager is dan"}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-[28px] font-black tabular-nums text-[hsl(var(--primary))] leading-none">
                  {pct}
                </span>
                <span className="text-[13px] font-bold text-[hsl(var(--primary))]">%</span>
                <span className="text-[11px] text-[hsl(var(--text-muted))] ml-1">
                  {t("pause_warning_of_shift") || "van de dienstduur"}
                </span>
              </div>
            </div>
            <input
              type="range" min={1} max={100} step={1}
              disabled={disabled}
              value={pct}
              onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
              className="w-full accent-[hsl(var(--primary))] cursor-pointer"
              data-testid="pause-warning-ratio"
              aria-label={t("pause_warning_slider_label")}
            />
            <div className="flex justify-between text-[10px] text-[hsl(var(--text-muted))] tabular-nums font-medium mt-1">
              <span>1%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Concrete example — makes the abstract percentage tangible */}
          <div className="rounded-2xl bg-[hsl(var(--primary-soft))] border hairline p-4" data-testid="pause-warning-example">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--primary))] mb-2">
              {t("pause_warning_example_title") || "Wat betekent dit concreet?"}
            </div>
            <div className="text-[13.5px] text-[hsl(var(--text-strong))] leading-relaxed">
              {(t("pause_warning_example_body")
                || "Voorbeeld: bij een dienst van {ref}u zie je de popup zodra de pauze lager is dan {min} min.")
                .replace("{ref}", refShiftHours)
                .replace("{min}", minBelow)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function computePause(hours, s) {
  if (!s.auto_pause_enabled) return 0;
  if (s.auto_pause_mode === "tiered") {
    const tiers = [...(s.auto_pause_tiers || [])].sort((a, b) => (b.hours || 0) - (a.hours || 0));
    for (const t of tiers) if (hours >= (t.hours || 0)) return t.minutes || 0;
    return 0;
  }
  return hours >= (s.auto_pause_threshold_hours ?? 5) ? (s.auto_pause_flat_minutes ?? 30) : 0;
}

function PausePreview({ settings, t }) {
  const samples = useMemo(() => [3, 5, 6.5, 8, 10], []);
  const warnPct = settings.pause_warning_ratio_percent ?? 5;
  const [tryHours, setTryHours] = useState(6);
  const tryMins = computePause(tryHours, settings);
  const tryNet = Math.max(0, tryHours - tryMins / 60);
  const enabled = settings.auto_pause_enabled;

  return (
    <aside className="lg:block" data-testid="pause-preview-card">
      <div className="sticky top-6 rounded-3xl border hairline overflow-hidden shadow-[var(--shadow-sm)]">
        {/* Header */}
        <div className="p-5 bg-gradient-to-br from-[hsl(38_92%_97%)] to-[hsl(45_92%_94%)] border-b hairline">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-xl bg-white grid place-items-center text-[hsl(28_92%_42%)]" style={{ boxShadow: "0 6px 16px -6px hsl(28 92% 45% / 0.35)" }}>
              <Coffee size={16} strokeWidth={1.9} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] leading-tight">
                {t("pause_preview_title_v2") || "Zo werkt jouw pauze-regel"}
              </div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">
                {t("pause_preview_subtitle") || "Live voorbeeld — updatet terwijl je wijzigt"}
              </div>
            </div>
          </div>
        </div>

        {/* Interactive tester — the "did-you-know" you can play with */}
        <div className="p-4 bg-white border-b hairline">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">
              {t("pause_try_it") || "Probeer het uit"}
            </div>
            <div className="text-[11px] text-[hsl(var(--text-muted))]">
              {t("pause_try_hint") || "Sleep de duur ↓"}
            </div>
          </div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-display text-[32px] font-bold tabular-nums text-[hsl(var(--text-strong))] leading-none">
              {tryHours.toFixed(1)}
            </span>
            <span className="text-[13px] text-[hsl(var(--text-muted))]">{t("pause_hours_worked") || "uur ingepland"}</span>
          </div>
          <input
            type="range" min={1} max={14} step={0.5}
            value={tryHours}
            onChange={(e) => setTryHours(parseFloat(e.target.value))}
            className="w-full accent-[hsl(28_92%_50%)] cursor-pointer"
            data-testid="pause-tester-slider"
            aria-label={t("pause_try_it") || "Probeer het uit"}
          />
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-[hsl(28_92%_96%)] p-2.5" data-testid="pause-tester-pause">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(28_92%_38%)]">{t("pause_label") || "Pauze"}</div>
              <div className="font-display text-[18px] font-bold tabular-nums text-[hsl(28_92%_35%)] leading-tight mt-0.5">
                {enabled && tryMins > 0 ? `${tryMins} min` : (t("no_pause") || "Geen")}
              </div>
            </div>
            <div className="rounded-xl bg-[hsl(var(--success-soft))] p-2.5" data-testid="pause-tester-net">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--success))]">{t("payroll_net") || "Netto"}</div>
              <div className="font-display text-[18px] font-bold tabular-nums text-[hsl(var(--success))] leading-tight mt-0.5">
                {tryNet.toFixed(1)}u
              </div>
            </div>
          </div>
        </div>

        {/* "Hoe werkt het?" — quick explainer */}
        <div className="p-4 bg-[hsl(var(--surface))] border-b hairline">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-lg bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0 mt-0.5">
              <Lightbulb size={13} strokeWidth={2.2} />
            </div>
            <div className="text-[11.5px] text-[hsl(var(--text-muted))] leading-relaxed">
              <span className="font-semibold text-[hsl(var(--text-strong))]">{t("pause_explain_title") || "Hoe werkt het?"}</span>{" "}
              {t("pause_explain_body") || "Rostr trekt de pauze automatisch van de dienst af zodra iemand langer dan de drempel werkt. De medewerker ziet netto-uren; jij ziet het bruto rooster."}
            </div>
          </div>
        </div>

        {/* Reference examples */}
        <div className="p-4 bg-white">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
            {t("pause_reference_examples") || "Voorbeelden"}
          </div>
          <div className="space-y-1">
            {samples.map((h) => {
              const mins = computePause(h, settings);
              const ratio = mins / (h * 60);
              const warn = ratio * 100 >= warnPct && mins > 0;
              return (
                <div key={h} className="flex items-center gap-2 py-1">
                  <span className="text-[12px] font-semibold tabular-nums text-[hsl(var(--text-strong))] w-14 shrink-0">
                    {Number.isInteger(h) ? `${h}u` : `${h.toFixed(1)}u`}
                  </span>
                  <ArrowRight size={12} className="text-[hsl(var(--text-muted))] shrink-0" />
                  <span className={`text-[12px] font-semibold tabular-nums ${mins > 0 ? "text-[hsl(28_92%_42%)]" : "text-[hsl(var(--text-muted))]"}`}>
                    {mins > 0 ? `${mins} min ${t("pause_label")?.toLowerCase() || "pauze"}` : (t("no_pause") || "Geen pauze")}
                  </span>
                  {warn && (
                    <span
                      className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-bold text-[hsl(var(--warning))]"
                      title={t("pause_warning_ratio_hint")}
                    >
                      <AlertTriangle size={10} strokeWidth={2.25} /> {t("high_short")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );
}
